/**
 * The world: floating origin, streamed terrain, airports and the ocean, as one thing.
 *
 * This is the seam between the simulation and the planet. The flight model works in a local NED
 * frame and knows nothing about latitude; the renderer works in a tangent plane and knows
 * nothing about the ellipsoid. This file owns the origin that connects them, and is the only
 * place that has to think about both.
 *
 * Per frame it:
 *   1. converts the aircraft's local position to geodetic, for the tile selector;
 *   2. rebases the origin if the aircraft has wandered more than `ORIGIN_REBASE_DISTANCE_M`
 *      (500 m — see `geo.ts` for why that number, which is a performance answer and not a
 *      precision one) from it;
 *   3. drives terrain streaming, the airport placement and the ocean;
 *   4. hands the flight model the ground elevation underneath it.
 *
 * ## The one subtlety: rebasing is not a translation
 *
 * When the origin moves, the east/north basis rotates as well — meridians converge. So nothing
 * anchored to the planet is *translated*: terrain tiles, airports and the ocean are all
 * re-derived from their own coordinates, which is exact. Only the aircraft's own local position
 * is shifted, and it is shifted to exactly zero, which is exact too. See `geodesy.ts`.
 *
 * `updateFixed(dt)`: {@link WorldSystem.updateFixed} keeps the origin under the aircraft and
 * refreshes the ground elevation, both on the physics tick because the gear needs them there.
 * `updateFrame(alpha)`: {@link WorldSystem.updateFrame} does selection, placement and the ocean.
 */

import { Group, Quaternion, Vector3 } from 'three';
import type { Material } from 'three';
import type { MeshStandardNodeMaterial } from 'three/webgpu';
import type { QualitySettings } from '../core/quality.js';
import { FloatingOrigin, makeGeodetic, type Geodetic } from './geodesy.js';
import { TerrainSystem } from './terrain/terrainSystem.js';
import {
  EoxImageryProvider,
  NaipImageryProvider,
  TerrariumElevationProvider,
  collectAttributions,
} from './terrain/provider.js';
import { AirportDatabase, type Airport } from './airports/db.js';
import { AirportScene } from './airports/runwayMesh.js';
import { airportSurfaceElevationM } from './airports/pad.js';

export interface WorldSystemOptions {
  settings: QualitySettings;
  /** The shared terrain material factory builds one material per tile; see terrainMaterial.ts. */
  createTileMaterial: (map: import('three').Texture | null) => Material;
  /** Set one tile material's geomorph weight. See `TerrainSystemOptions.setTileMorph`. */
  setTileMorph?: (material: Material, morph: number) => void;
  /** …and takes it back when the tile is evicted, so the factory's set stays bounded. */
  releaseTileMaterial: (material: Material) => void;
  /** How many tile-material node graphs the factory has actually built. See `terrainSystem.ts`. */
  materialsBuilt?: () => number;
  /** Push a tile's imagery to the GPU at adoption. See `terrainSystem.ts`. */
  warmTexture?: (texture: import('three').Texture) => void;
  /**
   * Applied to the airports' ground materials as they are built. See `AirportSceneOptions`.
   *
   * The terrain gets the same treatment through `createTileMaterial`, which already goes through
   * the renderer's factory; airports build their own materials, so they need their own hook.
   */
  decorateGroundMaterial?: (material: MeshStandardNodeMaterial) => void;
  /** True on the WebGL2 fallback, where 16-bit tile indices are genuinely half the memory. */
  use16BitIndices?: boolean;
  /** Where to start. */
  latitudeDeg: number;
  longitudeDeg: number;
  /** Injected for tests. */
  createWorker?: () => Worker;
}

const scratchPosition = /* #__PURE__ */ new Vector3();
const scratchRotation = /* #__PURE__ */ new Quaternion();
const scratchViewer = /* #__PURE__ */ new Vector3();

export class WorldSystem {
  readonly group = new Group();
  readonly origin: FloatingOrigin;
  readonly terrain: TerrainSystem;

  /** Every attribution the active data sources require, for the credits screen. */
  readonly attributions: string[];

  private readonly airportGroup = new Group();
  private readonly airportScenes = new Map<string, AirportScene>();
  private database: AirportDatabase | null = null;

  /** The aircraft's geodetic position, refreshed on the physics tick. */
  readonly aircraftGeodetic: Geodetic = makeGeodetic();
  /** Ground elevation under the aircraft [m MSL]; NaN until terrain has loaded there. */
  private groundElevationValue = Number.NaN;
  /** Sun elevation above the horizon [deg], for the runway lighting. */
  private sunElevationDeg = 45;

  private settings: QualitySettings;
  private rebaseCountValue = 0;
  private readonly decorateGroundMaterial:
    | ((material: MeshStandardNodeMaterial) => void)
    | undefined;

  constructor(options: WorldSystemOptions) {
    this.settings = options.settings;
    this.decorateGroundMaterial = options.decorateGroundMaterial;
    this.group.name = 'world';
    this.group.matrixAutoUpdate = false;
    this.airportGroup.name = 'airports';
    this.airportGroup.matrixAutoUpdate = false;

    this.origin = new FloatingOrigin(options.latitudeDeg, options.longitudeDeg);

    const elevation = new TerrariumElevationProvider();
    /*
     * Two imagery sources, tried in order per tile.
     *
     * NAIP is 1 m aerial imagery over the United States and public domain; Sentinel-2 is 10 m and
     * global. At the same zoom NAIP carries real detail where Sentinel-2 is magnifying — which is
     * the whole point, because a pilot at 4,000 ft has a display resolving about 1.4 m/px against
     * imagery supplying 7.57.
     *
     * NAIP first, Sentinel-2 behind it. Coverage is decided by the service answering or not,
     * rather than by a bounding box here that would have to stay correct as the programme reflies:
     * NAIP 404s outside the United States *and over open water*, and the second case is why a
     * bbox would not have been enough at a bayside airport.
     */
    const imagery = new NaipImageryProvider();
    const imageryFallback = new EoxImageryProvider();
    this.attributions = collectAttributions([elevation, imagery, imageryFallback]);
    this.attributions.push('Airport data: OurAirports.com (public domain)');

    this.terrain = new TerrainSystem({
      elevation,
      imagery,
      imageryFallback,
      origin: this.origin,
      settings: options.settings,
      createTileMaterial: options.createTileMaterial,
      ...(options.setTileMorph === undefined ? {} : { setTileMorph: options.setTileMorph }),
      releaseTileMaterial: options.releaseTileMaterial,
      ...(options.materialsBuilt === undefined ? {} : { materialsBuilt: options.materialsBuilt }),
      ...(options.warmTexture === undefined ? {} : { warmTexture: options.warmTexture }),
      use16BitIndices: options.use16BitIndices,
      createWorker: options.createWorker,
    });

    this.group.add(this.terrain.group, this.airportGroup);
  }

  /** Attach the airport database once it has downloaded. */
  setDatabase(database: AirportDatabase): void {
    this.database = database;
  }

  get airportDatabase(): AirportDatabase | null {
    return this.database;
  }

  get groundElevationM(): number {
    return this.groundElevationValue;
  }

  /**
   * The drawn airport surface under a point [m MSL], or `NaN` where there is none.
   *
   * Only the airports that have actually been *placed* are considered — the same set the renderer
   * has built geometry for — so the flight model can never stand on a pad that is not on screen.
   *
   * Called on the physics tick, and bounded: `showNearbyAirports` places at most six fields, so
   * this is at most a few dozen rectangle tests. Measured in the running sim it is 0.31 µs per
   * call with KSFO's four runways placed — 0.04 ms per *second* of flight at 120 Hz, against a
   * physics budget of 1 ms per frame. It allocates nothing.
   */
  airportSurfaceElevationM(latitudeDeg: number, longitudeDeg: number): number {
    let best = Number.NaN;
    for (const scene of this.airportScenes.values()) {
      const elevation = airportSurfaceElevationM(scene.airport.runways, latitudeDeg, longitudeDeg);
      if (Number.isNaN(elevation)) continue;
      if (Number.isNaN(best) || elevation > best) best = elevation;
    }
    return best;
  }

  get rebaseCount(): number {
    return this.rebaseCountValue;
  }

  setSunElevationDeg(degrees: number): void {
    this.sunElevationDeg = degrees;
  }

  applyQuality(settings: QualitySettings): void {
    this.settings = settings;
    this.terrain.applyQuality(settings);
  }

  /**
   * Keep the origin under the aircraft, and keep the ground elevation current.
   *
   * On the physics tick rather than the frame, because the landing gear reads
   * {@link groundElevationM} every tick and a value that lags the aircraft by a frame is a
   * value that makes the wheels sink into a hill at 200 kt.
   *
   * @returns true when the origin rebased, so the caller can shift the aircraft.
   */
  updateFixed(aircraftNorthM: number, aircraftEastM: number, aircraftDownM: number): boolean {
    this.origin.frame.nedToGeodetic(
      aircraftNorthM,
      aircraftEastM,
      aircraftDownM,
      this.aircraftGeodetic,
    );

    /*
     * The ground, and which of two disagreeing sources it comes from.
     *
     * An airport's pavement is drawn at OurAirports' *surveyed* threshold elevations; the terrain
     * under it is whatever SRTM sampled at 30 m posting. Those are different numbers — at KSFO
     * the DEM runs 0.25–0.71 m below the pavement along 10L — and until this existed the renderer
     * used one and the flight model the other. The aeroplane stood on the DEM while the runway
     * was drawn above it, so it sat with its wheels buried in the tarmac and the pilot's eye half
     * a metre under the surface it was looking along.
     *
     * So where an airport surface exists, it *is* the ground. Not a blend and not a preference
     * for whichever is higher: the pad is what the pilot can see, and the one thing the gear must
     * never disagree with is the picture.
     */
    const surface = this.airportSurfaceElevationM(
      this.aircraftGeodetic.latitudeDeg,
      this.aircraftGeodetic.longitudeDeg,
    );
    const sampled = Number.isNaN(surface)
      ? this.terrain.sampleGroundHeightM(
          this.aircraftGeodetic.latitudeDeg,
          this.aircraftGeodetic.longitudeDeg,
        )
      : surface;
    // Hold the last good value rather than flickering to NaN over an unloaded tile: the gear
    // would otherwise briefly see no ground at all and let the aircraft fall through it.
    if (!Number.isNaN(sampled)) this.groundElevationValue = sampled;

    const event = this.origin.update(aircraftEastM, aircraftNorthM);
    if (event === null) return false;

    this.rebaseCountValue = event.count;
    // Everything anchored to the planet re-derives; nothing is translated. Terrain tiles get
    // new transforms on the next frame, airports right now.
    this.placeAirports();
    this.terrain.onOriginRebase();
    return true;
  }

  /** Select and place terrain for this view. */
  updateFrame(
    viewportHeightPx: number,
    verticalFovRad: number,
    cameraAltitudeM: number,
    cameraRender: Vector3,
  ): void {
    this.terrain.updateFrame(
      this.aircraftGeodetic.latitudeDeg,
      this.aircraftGeodetic.longitudeDeg,
      Math.max(cameraAltitudeM, this.aircraftGeodetic.altitudeM),
      viewportHeightPx,
      verticalFovRad,
    );

    for (const scene of this.airportScenes.values()) {
      // The PAPI's colours depend on the angle the viewer sees it from, so each airport needs
      // the camera expressed in *its* frame: apply the rigid transform that takes a render-space
      // point in the origin's frame into this airport's.
      scene.frame.transformFromRender(this.origin.frame, scratchPosition, scratchRotation);
      scratchViewer.copy(cameraRender).applyQuaternion(scratchRotation).add(scratchPosition);
      scene.updateFrame(scratchViewer, this.sunElevationDeg);
    }
  }

  /**
   * Make an airport's scenery exist, building it the first time it is asked for.
   * Returns the record, or null when the database has not loaded or does not have it.
   */
  showAirport(icao: string): Airport | null {
    if (this.database === null) return null;
    const airport = this.database.byIcao(icao);
    if (airport === null) return null;

    if (!this.airportScenes.has(airport.icao)) {
      const scene = new AirportScene({
        airport,
        castShadows: this.settings.shadows.enabled,
        decorateGroundMaterial: this.decorateGroundMaterial,
      });
      this.airportScenes.set(airport.icao, scene);
      this.airportGroup.add(scene.group);
      scene.placeInto(this.origin.frame, scratchPosition, scratchRotation);
    }
    return airport;
  }

  /** Build scenery for everything within `radiusM` of the aircraft. */
  showNearbyAirports(radiusM = 60_000, limit = 6): void {
    if (this.database === null) return;
    for (const summary of this.database.nearest(
      this.aircraftGeodetic.latitudeDeg,
      this.aircraftGeodetic.longitudeDeg,
      limit,
      radiusM,
    )) {
      this.showAirport(summary.icao);
    }
  }

  /** Drop airport scenery that is far behind us, so a long flight does not accumulate it. */
  pruneAirports(radiusM = 120_000): void {
    if (this.database === null) return;
    for (const [icao, scene] of this.airportScenes) {
      const distanceM = haversineM(
        this.aircraftGeodetic.latitudeDeg,
        this.aircraftGeodetic.longitudeDeg,
        scene.airport.latitudeDeg,
        scene.airport.longitudeDeg,
      );
      if (distanceM <= radiusM) continue;
      scene.group.removeFromParent();
      scene.dispose();
      this.airportScenes.delete(icao);
    }
  }

  private placeAirports(): void {
    for (const scene of this.airportScenes.values()) {
      scene.placeInto(this.origin.frame, scratchPosition, scratchRotation);
    }
  }

  /**
   * Re-anchor the world somewhere else entirely — the spawn-at-a-new-airport path.
   * Throws away streamed terrain and built scenery, because none of it is near the new place.
   */
  teleport(latitudeDeg: number, longitudeDeg: number): void {
    this.origin.reanchor(latitudeDeg, longitudeDeg);
    this.terrain.reset();
    for (const scene of this.airportScenes.values()) {
      scene.group.removeFromParent();
      scene.dispose();
    }
    this.airportScenes.clear();
  }

  dispose(): void {
    this.terrain.dispose();
    for (const scene of this.airportScenes.values()) scene.dispose();
    this.airportScenes.clear();
    this.group.clear();
  }
}

function haversineM(latA: number, lonA: number, latB: number, lonB: number): number {
  const R = 6_371_008.8;
  const toRad = Math.PI / 180;
  const dLat = (latB - latA) * toRad;
  const dLon = (lonB - lonA) * toRad;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(latA * toRad) * Math.cos(latB * toRad) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
}
